import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, Switch, Button, Select, Badge, Tooltip, Space, Alert, Collapse, Radio } from 'antd';
import { 
  WarningOutlined, 
  FireOutlined, 
  ThunderboltOutlined,
  ReloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CarOutlined,
  CloudOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { HazardDataService, HAZARD_TYPES, HAZARD_SEVERITY } from '../../services/hazardDataService';
import WeatherLayerManager, { WEATHER_LAYERS as WLM_WEATHER_LAYERS, TIME_STEPS } from '../../services/weatherLayerManager';
import WeatherLegend from './WeatherLegend';
import WeatherPopup from './WeatherPopup';

// Use singleton instance to prevent multiple service instances
const hazardDataService = HazardDataService.getInstance();

const { Option } = Select;
const { Panel } = Collapse;

// HTML parsing functions will be defined inside the component for proper scope

/**
 * HazardOverlayLayer - Displays hazard data overlays on Google Maps with clustering
 * Integrates with both Organization Map and Crisis Map views
 * 🚀 NEW: Includes Google Maps TrafficLayer integration
 * 🌦️ NEW: Includes OpenWeatherMap weather layer overlays
 */
const HazardOverlayLayer = ({ 
  map, 
  mapBounds = null,
  showControls = true,
  height = 'auto',
  onHazardClick = null,
  onHazardCountChange = null,
  // Site icons toggle control
  showSiteIcons = true,
  onShowSiteIconsChange = null,
  // Site color mode toggle control
  siteColorMode = 'status',
  onSiteColorModeChange = null,
  // 🚨 MOBILE FIX: Accept overlayVisible as prop for mobile maps
  overlayVisible: overlayVisibleProp = null
}) => {
  const [filteredHazards, setFilteredHazards] = useState([]);
  
  // Notify parent of hazard count changes for loading overlay
  useEffect(() => {
    if (onHazardCountChange) {
      onHazardCountChange(filteredHazards.length);
    }
  }, [filteredHazards.length, onHazardCountChange]);
  const [selectedTypes, setSelectedTypes] = useState([
    HAZARD_TYPES.WEATHER,
    HAZARD_TYPES.FIRE,
    HAZARD_TYPES.EARTHQUAKE,
    HAZARD_TYPES.FLOOD,
    HAZARD_TYPES.CYCLONE,
    HAZARD_TYPES.TSUNAMI,
    HAZARD_TYPES.AIR_QUALITY,
    HAZARD_TYPES.TRAFFIC, // ✅ ON by default for traffic incidents + layer
    HAZARD_TYPES.TRANSPORT,
    HAZARD_TYPES.EMERGENCY
  ]);
  const [minSeverity, setMinSeverity] = useState(2);
  // 🚨 MOBILE FIX: Use prop if provided, otherwise use internal state
  const [overlayVisibleState, setOverlayVisibleState] = useState(true);
  const overlayVisible = overlayVisibleProp !== null ? overlayVisibleProp : overlayVisibleState;
  const [showLowSeverity, setShowLowSeverity] = useState(false);
  const [lowSeverityFeatureEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serviceInitialized, setServiceInitialized] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // 🚀 NEW: Traffic Layer State Management
  const [trafficLayerSupported, setTrafficLayerSupported] = useState(false);
  const [currentZoomLevel, setCurrentZoomLevel] = useState(5);
  
  // 🚦 Traffic layer visibility controlled by TRAFFIC selection in dropdown
  const trafficLayerVisible = selectedTypes.includes(HAZARD_TYPES.TRAFFIC);

    // 🌦️ ENHANCED: Weather Layer State Management with Time-Stepping
  const [activeWeatherLayers, setActiveWeatherLayers] = useState([]);
  const [currentTimeStep, setCurrentTimeStep] = useState('current');
  const [isWeatherAnimating, setIsWeatherAnimating] = useState(false);
  const [weatherLegends, setWeatherLegends] = useState([]);
  const [weatherPopupVisible, setWeatherPopupVisible] = useState(false);
  const [weatherPopupData, setWeatherPopupData] = useState(null);
  
  // Weather layer manager instance
  const weatherManagerRef = useRef(null);
  
  const markersRef = useRef([]);
  const markerClustererRef = useRef(null);
  const clusterMarkersRef = useRef([]); // Track manual cluster markers
  const infoWindowRef = useRef(null);
  const lastUpdateRef = useRef(0); // For throttling marker updates
  const lastProgressiveUpdateRef = useRef(0); // For throttling progressive updates
  const updateMarkersTimeoutRef = useRef(null); // For debouncing marker updates
  
  // 🚀 NEW: Traffic Layer References
  const trafficLayerRef = useRef(null);
  const mapListenersRef = useRef([]);
  const refreshTimeoutRef = useRef(null);

  // 🌦️ ENHANCED: Weather functionality moved to WeatherLayerManager

  // 🚀 NEW: Advanced bounds change detection for smarter refreshes
  const lastBoundsRef = useRef(null);
  const lastRefreshTimeRef = useRef(0);
  const MIN_REFRESH_INTERVAL = 2000; // Minimum 2 seconds between refreshes

  // Function definitions first
  const clearHazardMarkers = useCallback(() => {
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    
    // Clear manual cluster markers
    clusterMarkersRef.current.forEach(marker => marker.setMap(null));
    clusterMarkersRef.current = [];
    
    // Clear marker clusterer
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
      markerClustererRef.current = null;
    }
  }, []);

  // 🚀 NEW: Traffic Layer Management Functions
  const initializeTrafficLayer = useCallback(() => {
    if (!map || !window.google || !window.google.maps.TrafficLayer) {
      console.warn('🚦 TrafficLayer not available - Google Maps not loaded or TrafficLayer not supported');
      setTrafficLayerSupported(false);
      return;
    }

    try {
      if (!trafficLayerRef.current) {
        trafficLayerRef.current = new window.google.maps.TrafficLayer();
        console.log('🚦 TrafficLayer created successfully');
        setTrafficLayerSupported(true);
      }
    } catch (error) {
      console.error('🚦 Error creating TrafficLayer:', error);
      setTrafficLayerSupported(false);
    }
  }, [map]);

  // 🚀 ENHANCED: Advanced zoom level management with traffic density optimization
  const getTrafficVisibilityForZoom = useCallback((zoomLevel) => {
    // Traffic visibility rules based on zoom level
    if (zoomLevel >= 15) {
      return { show: true, reason: 'Street-level detail', density: 'high' };
    } else if (zoomLevel >= 12) {
      return { show: true, reason: 'City-level traffic', density: 'medium' };
    } else if (zoomLevel >= 10) {
      return { show: true, reason: 'Regional traffic', density: 'low' };
    } else if (zoomLevel >= 8) {
      return { show: false, reason: 'Traffic not useful at state level', density: 'none' };
    } else {
      return { show: false, reason: 'Traffic not visible at country level', density: 'none' };
    }
  }, []);

  const attachTrafficLayer = useCallback(() => {
    if (!trafficLayerRef.current || !map) return;

    try {
      const currentZoom = map.getZoom();
      const visibility = getTrafficVisibilityForZoom(currentZoom);
      
      if (visibility.show) {
        trafficLayerRef.current.setMap(map);
        console.log(`🚦 TrafficLayer attached at zoom ${currentZoom} - ${visibility.reason} (${visibility.density} density)`);
      } else {
        trafficLayerRef.current.setMap(null);
        console.log(`🚦 TrafficLayer hidden at zoom ${currentZoom} - ${visibility.reason}`);
      }
    } catch (error) {
      console.error('🚦 Error attaching TrafficLayer:', error);
    }
  }, [map, getTrafficVisibilityForZoom]);

  const detachTrafficLayer = useCallback(() => {
    if (!trafficLayerRef.current) return;

    try {
      trafficLayerRef.current.setMap(null);
      console.log('🚦 TrafficLayer detached');
    } catch (error) {
      console.error('🚦 Error detaching TrafficLayer:', error);
    }
  }, []);

  const refreshTrafficLayer = useCallback(() => {
    if (!trafficLayerVisible || !trafficLayerRef.current || !map) return;

    try {
      // Clear any existing refresh timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      // Debounce refresh to prevent overload on rapid movements
      refreshTimeoutRef.current = setTimeout(() => {
        const currentZoom = map.getZoom();
        const visibility = getTrafficVisibilityForZoom(currentZoom);
        
        if (visibility.show) {
          // Advanced refresh strategy: detach and reattach for fresh data
          trafficLayerRef.current.setMap(null);
          
          // Small delay to ensure clean detachment
          setTimeout(() => {
            if (trafficLayerRef.current && trafficLayerVisible) {
              trafficLayerRef.current.setMap(map);
              console.log(`🚦 TrafficLayer refreshed at zoom ${currentZoom} - ${visibility.reason}`);
            }
          }, 50);
        } else {
          trafficLayerRef.current.setMap(null);
          console.log(`🚦 TrafficLayer hidden at zoom ${currentZoom} - ${visibility.reason}`);
        }
      }, 300); // 300ms debounce
    } catch (error) {
      console.error('🚦 Error refreshing TrafficLayer:', error);
    }
  }, [trafficLayerVisible, map, getTrafficVisibilityForZoom]);

  // 🚀 NEW: Advanced bounds change detection for smarter refreshes
  const shouldRefreshTrafficLayer = useCallback(() => {
    if (!map || !trafficLayerVisible) return false;

    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current;

    // Don't refresh too frequently
    if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
      console.log('🚦 TrafficLayer refresh throttled - too soon since last refresh');
      return false;
    }

    // Check if bounds have changed significantly
    const currentBounds = map.getBounds();
    if (currentBounds && lastBoundsRef.current) {
      const ne1 = lastBoundsRef.current.getNorthEast();
      const sw1 = lastBoundsRef.current.getSouthWest();
      const ne2 = currentBounds.getNorthEast();
      const sw2 = currentBounds.getSouthWest();

      // Calculate bounds change threshold (5% of current view)
      const latDiff = Math.abs(ne2.lat() - ne1.lat());
      const lngDiff = Math.abs(ne2.lng() - ne1.lng());
      const currentLatSpan = ne2.lat() - sw2.lat();
      const currentLngSpan = ne2.lng() - sw2.lng();

      const changeThreshold = 0.05; // 5% change threshold
      const significantChange = 
        latDiff > (currentLatSpan * changeThreshold) ||
        lngDiff > (currentLngSpan * changeThreshold);

      if (!significantChange) {
        console.log('🚦 TrafficLayer refresh skipped - bounds change too small');
        return false;
      }
    }

    // Update last bounds and refresh time
    lastBoundsRef.current = currentBounds;
    lastRefreshTimeRef.current = now;
    return true;
  }, [map, trafficLayerVisible]);

  const smartRefreshTrafficLayer = useCallback(() => {
    if (!shouldRefreshTrafficLayer()) return;

    try {
      // Clear any existing refresh timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      // Smart debounced refresh
      refreshTimeoutRef.current = setTimeout(() => {
        const currentZoom = map.getZoom();
        const visibility = getTrafficVisibilityForZoom(currentZoom);
        
        if (visibility.show && trafficLayerVisible) {
          // Advanced refresh: Force fresh traffic data
          trafficLayerRef.current.setMap(null);
          
          // Brief pause for clean refresh
          setTimeout(() => {
            if (trafficLayerRef.current && trafficLayerVisible && map) {
              trafficLayerRef.current.setMap(map);
              console.log(`🚦 TrafficLayer smart refresh completed at zoom ${currentZoom} - ${visibility.reason}`);
            }
          }, 100);
        } else if (!visibility.show) {
          trafficLayerRef.current.setMap(null);
          console.log(`🚦 TrafficLayer hidden during smart refresh - ${visibility.reason}`);
        }
      }, 500); // Longer debounce for smart refresh
    } catch (error) {
      console.error('🚦 Error in smart TrafficLayer refresh:', error);
    }
  }, [shouldRefreshTrafficLayer, map, trafficLayerVisible, getTrafficVisibilityForZoom]);

  // 🚀 ENHANCED: Comprehensive cleanup with memory management
  const cleanupTrafficLayer = useCallback(() => {
    console.log('🧹 Starting comprehensive TrafficLayer cleanup...');
    
    try {
      let cleanupSteps = [];

      // Step 1: Clear all timeouts
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
        cleanupSteps.push('✅ Cleared refresh timeout');
      }

      // Step 2: Remove all map listeners
      if (mapListenersRef.current.length > 0) {
        mapListenersRef.current.forEach((listener, index) => {
          if (window.google && window.google.maps && window.google.maps.event) {
            try {
              window.google.maps.event.removeListener(listener);
              cleanupSteps.push(`✅ Removed listener ${index + 1}`);
            } catch (listenerError) {
              cleanupSteps.push(`⚠️ Error removing listener ${index + 1}: ${listenerError.message}`);
            }
          }
        });
        mapListenersRef.current = [];
        cleanupSteps.push('✅ Cleared listeners array');
      }

      // Step 3: Detach and destroy traffic layer
      if (trafficLayerRef.current) {
        try {
          trafficLayerRef.current.setMap(null);
          cleanupSteps.push('✅ Detached TrafficLayer from map');
          
          // Allow garbage collection
          trafficLayerRef.current = null;
          cleanupSteps.push('✅ Released TrafficLayer reference');
        } catch (layerError) {
          cleanupSteps.push(`⚠️ Error detaching TrafficLayer: ${layerError.message}`);
        }
      }

      // Step 4: Reset all tracking variables
      lastBoundsRef.current = null;
      lastRefreshTimeRef.current = 0;
      cleanupSteps.push('✅ Reset bounds tracking');
      cleanupSteps.push('✅ Reset refresh timing');

      // Step 5: Reset state variables
      setTrafficLayerSupported(false);
      setCurrentZoomLevel(5);
      cleanupSteps.push('✅ Reset state variables');

      // Log cleanup summary
      console.log('🧹 TrafficLayer cleanup completed:');
      cleanupSteps.forEach(step => console.log(`  ${step}`));
      
      return { success: true, steps: cleanupSteps };
      
    } catch (error) {
      const errorMsg = `🚦 Critical error during TrafficLayer cleanup: ${error.message}`;
      console.error(errorMsg, error);
      return { success: false, error: errorMsg };
    }
  }, []);



  // 🚀 NEW: Initialize Traffic Layer when map is available
  useEffect(() => {
    if (map && window.google) {
      initializeTrafficLayer();
    }
  }, [map, initializeTrafficLayer]);

  // 🚦 NEW: Handle traffic layer visibility based on TRAFFIC selection
  useEffect(() => {
    if (!trafficLayerSupported) return;

    console.log(`🚦 Traffic layer visibility changed: ${trafficLayerVisible ? 'ON' : 'OFF'} (controlled by TRAFFIC filter)`);
    
    if (trafficLayerVisible) {
      attachTrafficLayer();
    } else {
      detachTrafficLayer();
    }
  }, [trafficLayerVisible, trafficLayerSupported, attachTrafficLayer, detachTrafficLayer]);

  // 🚀 NEW: Setup map listeners for traffic layer auto-refresh
  useEffect(() => {
    if (!map || !trafficLayerSupported) return;

    // Clean up existing listeners
    if (mapListenersRef.current.length > 0) {
      mapListenersRef.current.forEach(listener => {
        window.google.maps.event.removeListener(listener);
      });
      mapListenersRef.current = [];
    }

    // Add new listeners with smart refresh
    try {
      const idleListener = map.addListener('idle', smartRefreshTrafficLayer);
      const zoomListener = map.addListener('zoom_changed', () => {
        setCurrentZoomLevel(map.getZoom());
        refreshTrafficLayer();
      });
      const boundsListener = map.addListener('bounds_changed', smartRefreshTrafficLayer);
      
      mapListenersRef.current = [idleListener, zoomListener, boundsListener];
      
      // Set initial zoom level
      setCurrentZoomLevel(map.getZoom());
      
      console.log('🚦 TrafficLayer smart listeners added (idle, zoom, bounds)');
    } catch (error) {
      console.error('🚦 Error adding TrafficLayer listeners:', error);
    }

    return () => {
      if (mapListenersRef.current.length > 0) {
        mapListenersRef.current.forEach(listener => {
          if (window.google && window.google.maps && window.google.maps.event) {
            window.google.maps.event.removeListener(listener);
          }
        });
        mapListenersRef.current = [];
      }
    };
  }, [map, trafficLayerSupported, refreshTrafficLayer, smartRefreshTrafficLayer]);

  // 🚀 ENHANCED: Comprehensive cleanup verification
  const verifyCleanupComplete = useCallback(() => {
    const checks = {
      timeout: refreshTimeoutRef.current === null,
      listeners: mapListenersRef.current.length === 0,
      trafficLayer: trafficLayerRef.current === null,
      bounds: lastBoundsRef.current === null,
      refreshTime: lastRefreshTimeRef.current === 0
    };

    const allClean = Object.values(checks).every(Boolean);
    
    console.log('🔍 TrafficLayer cleanup verification:', {
      ...checks,
      overallStatus: allClean ? '✅ CLEAN' : '⚠️ INCOMPLETE'
    });

    return allClean;
  }, []);

  // 🚀 ENHANCED: Cleanup on component unmount with verification
  useEffect(() => {
    return () => {
      console.log('🚪 HazardOverlayLayer unmounting - cleaning up TrafficLayer...');
      const cleanupResult = cleanupTrafficLayer();
      
      // Verify cleanup after a brief delay
      setTimeout(() => {
        const isClean = verifyCleanupComplete();
        if (!isClean) {
          console.warn('⚠️ TrafficLayer cleanup may be incomplete - check for memory leaks');
        } else {
          console.log('✅ TrafficLayer cleanup verified complete');
        }
      }, 100);
    };
  }, [cleanupTrafficLayer, verifyCleanupComplete]);

  const getHazardIconSymbol = useCallback((type) => {
    const symbols = {
      [HAZARD_TYPES.WEATHER]: '⛈️',
      [HAZARD_TYPES.FIRE]: '🔥',
      [HAZARD_TYPES.EARTHQUAKE]: '🌋',
      [HAZARD_TYPES.FLOOD]: '🌊',
      [HAZARD_TYPES.CYCLONE]: '🌀',
      [HAZARD_TYPES.TSUNAMI]: '🌊',
      [HAZARD_TYPES.AIR_QUALITY]: '💨',
      [HAZARD_TYPES.TRAFFIC]: '🚗',
      [HAZARD_TYPES.TRANSPORT]: '🚇',
      [HAZARD_TYPES.EMERGENCY]: '🚨',
      [HAZARD_TYPES.AMBULANCE]: '🚑'
    };
    return symbols[type] || '⚠️';
  }, []);

  /**
   * Parse HTML content to readable text for hazard descriptions
   * Handles both plain text and HTML content from various emergency feeds
   */
  const parseHazardDescription = useCallback((description) => {
    if (!description) return 'No description available';
    
    console.log('🔍 Parsing hazard description (original):', {
      original: description.substring(0, 100) + (description.length > 100 ? '...' : ''),
      hasEncodedHTML: /&lt;[^&]*&gt;/g.test(description)
    });
    
    // First decode HTML entities to reveal actual HTML tags
    let decodedDescription = description
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/&mdash;/g, '—')
      .replace(/&ndash;/g, '–')
      .replace(/&deg;/g, '°')
      .replace(/&copy;/g, '©')
      .replace(/&reg;/g, '®')
      .replace(/&trade;/g, '™');
    
    console.log('🔍 After HTML entity decoding:', {
      decoded: decodedDescription.substring(0, 100) + (decodedDescription.length > 100 ? '...' : ''),
      hasHTML: /<[^>]+>/g.test(decodedDescription)
    });
    
    // Now check if the decoded description contains HTML tags
    const hasHTML = /<[^>]+>/g.test(decodedDescription);
    
    if (!hasHTML) {
      // Plain text - return as is (but clean up extra whitespace)
      const cleaned = decodedDescription.replace(/\s+/g, ' ').trim();
      console.log('🔍 Plain text result:', cleaned.substring(0, 100) + '...');
      return cleaned;
    }
    
    // HTML content - parse and format nicely (using decoded description)
    let parsed = decodedDescription;
    
    // Convert common HTML tags to readable format
    parsed = parsed
      // Convert <strong> tags to bold text markers
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
      // Convert <b> tags to bold text markers
      .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
      // Convert <br> and <br/> tags to line breaks
      .replace(/<br\s*\/?>/gi, '\n')
      // Convert <p> tags to paragraph breaks
      .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n')
      // Convert <div> tags to line breaks
      .replace(/<div[^>]*>(.*?)<\/div>/gi, '$1\n')
      // Convert <span> tags (remove but keep content)
      .replace(/<span[^>]*>(.*?)<\/span>/gi, '$1')
      // Remove any remaining HTML tags
      .replace(/<[^>]+>/g, '')
      // Clean up extra whitespace and line breaks (HTML entities already decoded above)
      .replace(/\n\s*\n/g, '\n\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s+/g, ' ')
      .trim();
    
    // If the parsed content is too long, truncate it
    if (parsed.length > 600) {
      parsed = parsed.substring(0, 600) + '...';
    }
    
    console.log('🔍 HTML parsed result:', parsed.substring(0, 100) + '...');
    return parsed;
  }, []);

  /**
   * Format parsed description for HTML display in InfoWindow
   * Converts text formatting markers back to HTML for display
   */
  const formatDescriptionForDisplay = useCallback((description) => {
    if (!description) return 'No description available';
    
    const formatted = description
      // Convert bold markers to HTML
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Convert line breaks to HTML
      .replace(/\n/g, '<br/>')
      // Ensure proper spacing
      .replace(/\s+/g, ' ')
      .trim();
    
    console.log('🔍 Final formatted result:', formatted.substring(0, 100) + '...');
    return formatted;
  }, []);

  /**
   * Generate data age display for hazard popup - TASK 3 IMPLEMENTATION
   */
  const getDataAgeDisplay = useCallback((hazard) => {
    const now = Date.now();
    const dataAge = Math.round((now - hazard.timestamp) / (1000 * 60)); // minutes
    const lastUpdated = hazard.lastUpdated || hazard.timestamp;
    const updateAge = Math.round((now - lastUpdated) / (1000 * 60)); // minutes
    
    // Check if data is stale or failed to refresh
    const isStale = dataAge > 30; // Consider data stale after 30 minutes
    const failedRefresh = hazard.refreshFailed || (hazard.source && updateAge > 60);
    
    let statusText, statusColor, statusIcon;
    
    if (failedRefresh || isStale) {
      statusText = "Unable to verify at last refresh (cached data shown)";
      statusColor = "#ff4d4f";
      statusIcon = "⚠️";
    } else {
      const timestamp = new Date(lastUpdated).toLocaleString();
      statusText = timestamp;
              statusColor = dataAge < 15 ? "#52c41a" : dataAge < 30 ? "#fa8c16" : "#ff4d4f";
      statusIcon = dataAge < 15 ? "✅" : dataAge < 30 ? "🟡" : "🔴";
    }
    
    return {
      statusText,
      statusColor,
      statusIcon,
      dataAge,
      isStale: isStale || failedRefresh
    };
  }, []);

  const showHazardInfo = useCallback((hazards, marker) => {
    // Handle both single hazard and array of hazards
    const hazardArray = Array.isArray(hazards) ? hazards : [hazards];
    
    console.log('🔍 showHazardInfo called with hazards:', hazardArray.map(h => ({ title: h.title, hasDescription: !!h.description })));
    
    const content = `
      <div style="max-width: 350px; font-family: Arial, sans-serif;">
        ${hazardArray.length > 1 ? `
          <div style="margin-bottom: 12px; padding: 8px; background: #f0f0f0; border-radius: 4px;">
            <strong style="color: #1890ff;">📍 ${hazardArray.length} Hazards at this Location</strong>
          </div>
        ` : ''}
        
        ${hazardArray.map((hazard, index) => {
          const ageDisplay = getDataAgeDisplay(hazard);
          
          return `
            <div style="margin-bottom: ${index < hazardArray.length - 1 ? '16px' : '8px'}; ${index > 0 ? 'border-top: 1px solid #eee; padding-top: 12px;' : ''}">
              <div style="display: flex; align-items: center; margin-bottom: 8px;">
                <span style="font-size: 20px; margin-right: 8px;">${getHazardIconSymbol(hazard.type)}</span>
                <h3 style="margin: 0; color: ${hazard.severity?.color || '#333'}; font-size: 16px;">${hazard.title || 'Unknown Hazard'}</h3>
              </div>
              
              <div style="margin-bottom: 8px;">
                <span style="background: ${hazard.severity?.color || '#999'}; color: white; padding: 2px 6px; border-radius: 4px; font-size: 12px; font-weight: bold;">
                  ${(hazard.severity?.label || 'Unknown').toUpperCase()}
                </span>
                <span style="margin-left: 8px; color: #666; font-size: 12px;">
                  ${hazard.metadata?.sources && hazard.metadata.sources.length > 1 
                    ? `Sources: ${hazard.metadata.sources.map(s => s.toUpperCase()).join(' + ')}`
                    : hazard.source || 'Unknown Source'
                  }
                </span>
              </div>
              
              <p style="margin: 8px 0; font-size: 14px; line-height: 1.4;">
                ${formatDescriptionForDisplay(parseHazardDescription(hazard.description))}
              </p>
              
              ${Object.keys(hazard.metadata || {}).length > 0 ? `
                <div style="background: #f5f5f5; padding: 8px; border-radius: 4px; margin: 8px 0;">
                  ${Object.entries(hazard.metadata).map(([key, value]) => {
                    // Skip internal fields and sourceUrls (handled separately)
                    if (['sources', 'primarySource', 'sourceUrls', 'isAustralian'].includes(key)) {
                      return '';
                    }
                    return `
                      <div style="font-size: 12px; margin: 2px 0;">
                        <strong>${key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> ${value}
                      </div>
                    `;
                  }).join('')}
                  
                  ${hazard.metadata.sourceUrls ? `
                    <div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid #ddd;">
                      <div style="font-size: 11px; color: #666; font-weight: bold; margin-bottom: 4px;">External Links:</div>
                      ${Object.entries(hazard.metadata.sourceUrls).map(([source, url]) => `
                        <div style="margin: 2px 0;">
                          <a href="${url}" target="_blank" style="color: #1890ff; text-decoration: none; font-size: 11px;">
                            📋 ${source.toUpperCase()} Details →
                          </a>
                        </div>
                      `).join('')}
                    </div>
                  ` : ''}
                </div>
              ` : ''}
              
              <!-- TASK 3: Enhanced Data Age Display -->
              <div style="background: ${ageDisplay.isStale ? '#fff2f0' : '#f6ffed'}; border: 1px solid ${ageDisplay.statusColor}; border-radius: 4px; padding: 8px; margin: 8px 0;">
                <div style="display: flex; align-items: center; font-size: 12px; font-weight: 500;">
                  <span style="margin-right: 6px;">${ageDisplay.statusIcon}</span>
                  <span style="color: ${ageDisplay.statusColor};">Last Verified:</span>
                </div>
                <div style="font-size: 11px; color: ${ageDisplay.statusColor}; margin-top: 2px; font-weight: 500;">
                  ${ageDisplay.statusText}
                </div>
                ${ageDisplay.dataAge > 0 ? `
                  <div style="font-size: 10px; color: #999; margin-top: 4px;">
                    Data age: ${ageDisplay.dataAge} minutes
                  </div>
                ` : ''}
              </div>
            </div>
          `;
        }).join('')}
        
        ${hazardArray[0].expiresAt ? `
          <div style="font-size: 11px; color: #999; margin-top: 8px; border-top: 1px solid #eee; padding-top: 6px;">
            <div>Expires: ${Math.round((hazardArray[0].expiresAt - Date.now()) / (1000 * 60))} minutes</div>
          </div>
        ` : ''}
      </div>
    `;

    infoWindowRef.current.setContent(content);
    infoWindowRef.current.open(map, marker);
  }, [getHazardIconSymbol, map, getDataAgeDisplay, parseHazardDescription, formatDescriptionForDisplay]);

  const updateMapMarkers = useCallback(() => {
    if (!map || !window.google) {
      console.log('🚫 updateMapMarkers skipped - map or google not available');
      return;
    }

    // Add throttling to prevent rapid re-execution
    const now = Date.now();
    if (now - lastUpdateRef.current < 100) { // Throttle to max 10 times per second
      console.log('🚫 Throttling updateMapMarkers - too frequent calls');
      return;
    }
    lastUpdateRef.current = now;

    console.log(`🗺️ Starting updateMapMarkers - filteredHazards: ${filteredHazards.length}, overlayVisible: ${overlayVisible}`);

    // Clear existing markers and clusterer
    clearHazardMarkers();

    // Create info window if it doesn't exist
    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    console.log(`🗺️ Creating markers for ${filteredHazards.length} hazards with clustering`);

    // Create individual markers for each hazard
    const newMarkers = filteredHazards.map((hazard, index) => {
      // 🔺 MOBILE FIX: Use triangles for hazards on mobile to distinguish from building site markers
      const isMobile = window.innerWidth <= 768;
      const hazardIcon = isMobile ? {
        // Triangle path for mobile hazard markers
        path: 'M 0,-20 L -17,15 L 17,15 Z',
        fillColor: hazard.severity?.color || '#999',
        fillOpacity: 0.9,
        stroke: true,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 0.8,
        anchor: { x: 0, y: 0 }
      } : {
        // Circle for desktop (unchanged)
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: hazard.severity?.color || '#999',
          fillOpacity: 0.8,
          stroke: true,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          scale: 12
      };

      console.log(`🔺 ${isMobile ? 'MOBILE' : 'DESKTOP'} HAZARD MARKER: Creating ${isMobile ? 'triangle' : 'circle'} for "${hazard.title}"`);

      const marker = new window.google.maps.Marker({
        position: { lat: hazard.latitude, lng: hazard.longitude },
        title: hazard.title || 'Unknown Hazard',
        icon: hazardIcon
      });

      // Add click listener
      marker.addListener('click', () => {
        showHazardInfo(hazard, marker);
        if (onHazardClick) {
          onHazardClick(hazard);
        }
      });

      // Store hazard data with marker
      marker.hazardData = hazard;
      
      return marker;
    });

    // Try to use MarkerClusterer if available, otherwise fall back to manual clustering
    if (window.google.maps.MarkerClusterer || window.MarkerClusterer) {
      try {
        // Use the newer @googlemaps/markerclusterer if available
        const MarkerClustererClass = window.google.maps.MarkerClusterer || window.MarkerClusterer;
        
        markerClustererRef.current = new MarkerClustererClass({
          map: map,
          markers: newMarkers,
          gridSize: 120,
          maxZoom: 12,
          minimumClusterSize: 2,
          styles: [
            {
              url: 'data:image/svg+xml;base64,' + btoa(`
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                  <circle cx="20" cy="20" r="18" fill="#1890ff" stroke="#fff" stroke-width="2" opacity="0.9"/>
                  <text x="20" y="26" text-anchor="middle" font-family="Arial" font-size="12" font-weight="bold" fill="white">TEXT</text>
                </svg>
              `),
              height: 40,
              width: 40,
              textColor: 'white',
              textSize: 12
            },
            {
              url: 'data:image/svg+xml;base64,' + btoa(`
                <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
                  <circle cx="25" cy="25" r="23" fill="#fa8c16" stroke="#fff" stroke-width="2" opacity="0.9"/>
                  <text x="25" y="31" text-anchor="middle" font-family="Arial" font-size="14" font-weight="bold" fill="white">TEXT</text>
                </svg>
              `),
              height: 50,
              width: 50,
              textColor: 'white',
              textSize: 14
            },
            {
              url: 'data:image/svg+xml;base64,' + btoa(`
                <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60">
                  <circle cx="30" cy="30" r="28" fill="#f5222d" stroke="#fff" stroke-width="3" opacity="0.9"/>
                  <text x="30" y="37" text-anchor="middle" font-family="Arial" font-size="16" font-weight="bold" fill="white">TEXT</text>
                </svg>
              `),
              height: 60,
              width: 60,
              textColor: 'white',
              textSize: 16
            }
          ]
        });

        console.log(`✅ Created ${newMarkers.length} markers with MarkerClusterer`);
      } catch (error) {
        console.warn('⚠️ MarkerClusterer failed, using manual clustering:', error);
        // Set a flag to prevent repeated attempts at clustering
        if (!window.markerClustererFailed) {
          window.markerClustererFailed = true;
        createManualClusters(newMarkers);
        } else {
          // Just show individual markers if clustering keeps failing
          newMarkers.forEach(marker => marker.setMap(map));
        }
      }
    } else {
      console.warn('⚠️ MarkerClusterer not available, using manual clustering');
      // Set a flag to prevent repeated warnings
      if (!window.markerClustererUnavailable) {
        window.markerClustererUnavailable = true;
      createManualClusters(newMarkers);
      } else {
        // Just show individual markers if clustering is unavailable
        newMarkers.forEach(marker => marker.setMap(map));
      }
    }

    markersRef.current = newMarkers;
  }, [map, filteredHazards, overlayVisible, clearHazardMarkers, showHazardInfo, onHazardClick]);

  // Helper function to get cluster center
  const getClusterCenter = useCallback((markers) => {
    let lat = 0, lng = 0;
    markers.forEach(marker => {
      const pos = marker.getPosition();
      lat += pos.lat();
      lng += pos.lng();
    });
    return {
      lat: lat / markers.length,
      lng: lng / markers.length
    };
  }, []);

  // Manual clustering fallback function
  const createManualClusters = useCallback((markers) => {
    // Clear any existing cluster markers first
    clusterMarkersRef.current.forEach(marker => marker.setMap(null));
    clusterMarkersRef.current = [];

    // Get current zoom level to adjust clustering aggressiveness
    const currentZoom = map.getZoom();
    
    // At high zoom levels, show individual markers instead of clustering
    if (currentZoom > 12) {
      console.log(`🎯 Zoom level ${currentZoom} - showing individual markers`);
      markers.forEach(marker => marker.setMap(map));
      return;
    }
    
    // More aggressive clustering at lower zoom levels
    let clusterDistance;
    if (currentZoom <= 6) {
      clusterDistance = 50000; // 50km at country level
    } else if (currentZoom <= 8) {
      clusterDistance = 20000; // 20km at state level
    } else if (currentZoom <= 10) {
      clusterDistance = 10000; // 10km at regional level
    } else {
      clusterDistance = 5000;  // 5km at city level
    }

    console.log(`🎯 Manual clustering at zoom ${currentZoom} with ${clusterDistance}m distance`);

    const clusters = [];
    const processed = new Set();

    markers.forEach((marker, index) => {
      if (processed.has(index)) return;

      const cluster = [marker];
      const markerPos = marker.getPosition();
      
      // Find nearby markers within cluster distance
      markers.forEach((otherMarker, otherIndex) => {
        if (index === otherIndex || processed.has(otherIndex)) return;
        
        const otherPos = otherMarker.getPosition();
        const distance = window.google.maps.geometry.spherical.computeDistanceBetween(markerPos, otherPos);
        
        // If within cluster distance, add to cluster
        if (distance < clusterDistance) {
          cluster.push(otherMarker);
          processed.add(otherIndex);
        }
      });

      processed.add(index);
      clusters.push(cluster);
    });

    // Create cluster markers or individual markers
    clusters.forEach(cluster => {
      if (cluster.length === 1) {
        // Single marker - add directly to map
        cluster[0].setMap(map);
      } else {
        // Multiple markers - create cluster marker with size based on count
        const center = getClusterCenter(cluster);
        const hazards = cluster.map(m => m.hazardData);
        
        // Scale cluster marker based on count
        let scale, color;
        if (cluster.length < 10) {
          scale = 20;
          color = '#1890ff';
        } else if (cluster.length < 50) {
          scale = 25;
          color = '#fa8c16';
        } else {
          scale = 30;
          color = '#f5222d';
        }
        
        const clusterMarker = new window.google.maps.Marker({
          position: center,
          map: map,
          title: `${cluster.length} hazards`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: color,
            fillOpacity: 0.9,
            stroke: true,
            strokeColor: '#ffffff',
            strokeWeight: 3,
            scale: scale
          },
          label: {
            text: cluster.length.toString(),
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold'
          },
          zIndex: 1000 + cluster.length // Higher clusters appear on top
        });

        clusterMarker.addListener('click', () => {
          showHazardInfo(hazards, clusterMarker);
        });

        // Track cluster markers so we can clear them later
        clusterMarkersRef.current.push(clusterMarker);
      }
    });

    console.log(`✅ Created ${clusters.length} manual clusters from ${markers.length} markers`);
  }, [map, showHazardInfo, getClusterCenter]);

  const loadHazards = useCallback(() => {
    try {
      // 🚨 MOBILE DEBUG: Enhanced hazard loading logging
      const isMobile = window.innerWidth <= 768;
      console.log(`🚨 ${isMobile ? 'MOBILE' : 'DESKTOP'} HAZARD LOAD: Starting loadHazards...`);
      console.log(`🚨 HAZARD LOAD: Map bounds:`, mapBounds);
      console.log(`🚨 HAZARD LOAD: Selected types:`, selectedTypes);
      console.log(`🚨 HAZARD LOAD: Show low severity:`, showLowSeverity);
      
      // 🚑 FIXED: Pass selectedTypes to service layer for proper ambulance filtering
      const allHazards = hazardDataService.getAllHazards(mapBounds, selectedTypes, 15, showLowSeverity);
      console.log(`🚨 ${isMobile ? 'MOBILE' : 'DESKTOP'} HAZARD LOAD: Service returned ${allHazards.length} hazards`);
      
      const filtered = allHazards.filter(hazard => {
        // 🕐 EARTHQUAKE TIME FILTER: Only show earthquake incidents for 24 hours after they occurred
        if (hazard.type === 'earthquake' && hazard.timestamp) {
          const earthquakeTime = hazard.timestamp;
          const now = Date.now();
          const twentyFourHoursMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
          
          // Filter out earthquakes older than 24 hours
          if (now - earthquakeTime > twentyFourHoursMs) {
            console.log(`🕐 Filtering out earthquake "${hazard.title}" - occurred ${Math.round((now - earthquakeTime) / (60 * 60 * 1000))} hours ago`);
            return false;
          }
        }
        
        // When low severity toggle is ON, service layer already handled severity filtering
        if (showLowSeverity) {
          return true; // Show all hazards from service (including low severity)
        }
        // When toggle is OFF, apply normal minSeverity filtering
        const severityMatch = (hazard.severity?.level || 1) >= minSeverity;
        return severityMatch;
      });
      
      // TEMP: Add some mock hazards to test grouping functionality
      const mockHazards = [];
      
      const finalHazards = [...filtered, ...mockHazards];
      setFilteredHazards(finalHazards);
      setLastUpdate(Date.now());
      setError(null);
      
      // Call onHazardCountChange if provided
      if (typeof onHazardCountChange === 'function') {
        onHazardCountChange(finalHazards.length);
      }
      
      console.log(`🚨 ${window.location.pathname.includes('incidents') ? 'Crisis-Map' : 'Dashboard'}: Loaded ${allHazards.length} hazards, ${filtered.length} after filtering, ${finalHazards.length} total`);
    } catch (error) {
      console.error('🚨 Error loading hazards:', error);
      setError('Failed to load hazard data');
    }
  }, [mapBounds, selectedTypes, minSeverity, showLowSeverity]);

  // 🚀 NEW: Progressive loading callback for real-time hazard updates
  const onProgressiveUpdate = useCallback((progressData) => {
    console.log(`🚀 PROGRESSIVE UI UPDATE: ${progressData.latestSource} completed (${progressData.completedSources}/${progressData.totalSources})`);
    
    // Add throttling to prevent rapid updates that cause marker flickering
    const now = Date.now();
    if (now - lastProgressiveUpdateRef.current < 500) { // Throttle to max 2 updates per second
      console.log('🚫 Throttling progressive update - too frequent');
      return;
    }
    lastProgressiveUpdateRef.current = now;
    
    // Update hazards in real-time as each source completes
    if (progressData.currentHazards && progressData.currentHazards.length > 0) {
      const filtered = progressData.currentHazards.filter(hazard => {
        // 🕐 EARTHQUAKE TIME FILTER: Only show earthquake incidents for 24 hours after they occurred
        if (hazard.type === 'earthquake' && hazard.timestamp) {
          const earthquakeTime = hazard.timestamp;
          const now = Date.now();
          const twentyFourHoursMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
          
          // Filter out earthquakes older than 24 hours
          if (now - earthquakeTime > twentyFourHoursMs) {
            console.log(`🕐 Progressive update: Filtering out earthquake "${hazard.title}" - occurred ${Math.round((now - earthquakeTime) / (60 * 60 * 1000))} hours ago`);
            return false;
          }
        }
        
        // Apply same filtering logic as loadHazards
        if (showLowSeverity) {
          return true;
        }
        const severityMatch = (hazard.severity?.level || 1) >= minSeverity;
        return severityMatch;
      });
      
      // Update UI immediately with new hazards
      setFilteredHazards(filtered);
      console.log(`🚀 PROGRESSIVE UI: Updated to ${filtered.length} hazards after ${progressData.latestSource}`);
    }
  }, [showLowSeverity, minSeverity]);

  const initializeHazardService = useCallback(async () => {
    // Prevent re-initialization if already initialized or in progress
    if (serviceInitialized || loading) {
      console.log('🔄 Hazard service already initialized or initializing, skipping...');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('🚨 PROGRESSIVE INIT: Initializing hazard overlay service...');
      
      await hazardDataService.initialize();
      setServiceInitialized(true);
      
      // 🚀 CRITICAL: Use progressive loading during initialization for immediate hazard display
      console.log('🚨 PROGRESSIVE INIT: Service initialized, starting progressive hazard loading...');
      if (map && overlayVisible) {
        // Use progressive callback during initialization for fastest first display
        await hazardDataService.refreshAllSources(onProgressiveUpdate);
        // Final load to ensure consistency
        loadHazards();
      }
    } catch (error) {
      console.error('🚨 Failed to initialize hazard service:', error);
      setError('Failed to initialize hazard monitoring service. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  }, [serviceInitialized, loading, map, overlayVisible, loadHazards, onProgressiveUpdate]);

  // 🌦️ ENHANCED: Weather Layer Management with WeatherLayerManager
  const initializeWeatherManager = useCallback(() => {
    if (!map) return;

    // Create weather manager instance
    weatherManagerRef.current = new WeatherLayerManager(
      map,
      setWeatherLegends, // Legend update callback
      setCurrentTimeStep // Time step change callback
    );

    // Initialize the manager
    const initialized = weatherManagerRef.current.initialize();
    if (initialized) {
      console.log('🌦️ Enhanced weather layer manager initialized');
    }

    return initialized;
  }, [map]);

  const handleWeatherLayerChange = useCallback((layerTypes) => {
    setActiveWeatherLayers(layerTypes || []);
    if (weatherManagerRef.current) {
      weatherManagerRef.current.attachWeatherLayers(layerTypes || []);
    }
  }, []);

  const handleTimeStepChange = useCallback((timeStep) => {
    setCurrentTimeStep(timeStep);
    if (weatherManagerRef.current) {
      weatherManagerRef.current.setTimeStep(timeStep);
    }
  }, []);

  const handleWeatherAnimation = useCallback(() => {
    if (!weatherManagerRef.current) return;

    if (isWeatherAnimating) {
      weatherManagerRef.current.stopAnimation();
      setIsWeatherAnimating(false);
    } else {
      weatherManagerRef.current.startAnimation();
      setIsWeatherAnimating(true);
    }
  }, [isWeatherAnimating]);

  const handleSiteWeatherPopup = useCallback((latitude, longitude, siteName) => {
    if (!weatherManagerRef.current) return;

    setWeatherPopupData({ latitude, longitude, siteName });
    setWeatherPopupVisible(true);
  }, []);

  // useEffect hooks after function definitions
  // Initialize hazard service when map becomes available
  useEffect(() => {
    if (map && !serviceInitialized && !loading) {
      // 🚨 MOBILE DEBUG: Enhanced initialization logging
      const isMobile = window.innerWidth <= 768;
      console.log(`🚨 ${isMobile ? 'MOBILE' : 'DESKTOP'} HAZARD INIT: Starting hazard service initialization...`);
      console.log('🚨 HAZARD INIT: Map instance:', !!map);
      console.log('🚨 HAZARD INIT: Map bounds:', mapBounds);
      console.log('🚨 HAZARD INIT: Overlay visible:', overlayVisible);
      console.log('🚨 HAZARD INIT: Show controls:', showControls);
      console.log('🚨 HAZARD INIT: Selected types:', selectedTypes);
    initializeHazardService();
    }
    
    return () => {
      // Cleanup on unmount
      clearHazardMarkers();
    };
  }, [map, serviceInitialized, loading, initializeHazardService, clearHazardMarkers]); // Wait for map to be available

  // Add zoom change listener for re-clustering
  useEffect(() => {
    if (!map) return;

    let zoomTimeout;
    const zoomChangeListener = map.addListener('zoom_changed', () => {
      // Debounce zoom changes to prevent rapid re-clustering
      if (zoomTimeout) {
        clearTimeout(zoomTimeout);
      }
      
      zoomTimeout = setTimeout(() => {
        // Re-cluster markers when zoom changes - access current hazards from ref
        if (markersRef.current.length > 0 && overlayVisible) {
        console.log('🔍 Zoom changed, re-clustering markers...');
          updateMapMarkers();
      }
      }, 500); // Wait 500ms after zoom stops changing
    });

    return () => {
      if (zoomChangeListener) {
        window.google.maps.event.removeListener(zoomChangeListener);
      }
      if (zoomTimeout) {
        clearTimeout(zoomTimeout);
      }
    };
  }, [map, overlayVisible, updateMapMarkers]);

  // Update hazards when filters change
  useEffect(() => {
    if (map && overlayVisible && serviceInitialized) {
      // 🚨 MOBILE DEBUG: Enhanced filter change logging
      const isMobile = window.innerWidth <= 768;
      console.log(`🔄 ${isMobile ? 'MOBILE' : 'DESKTOP'} Filter change detected - reloading hazards with:`, {
        selectedTypes,
        minSeverity,
        showLowSeverity,
        mapBounds: !!mapBounds,
        hasBounds: !!mapBounds,
        boundsDetails: mapBounds
      });
      loadHazards();
    } else {
      // 🚨 MOBILE DEBUG: Log why hazards aren't loading
      const isMobile = window.innerWidth <= 768;
      console.log(`🚫 ${isMobile ? 'MOBILE' : 'DESKTOP'} Hazards NOT loading because:`, {
        hasMap: !!map,
        overlayVisible,
        serviceInitialized,
        allConditionsMet: !!(map && overlayVisible && serviceInitialized)
      });
    }
  }, [map, mapBounds, selectedTypes, minSeverity, showLowSeverity, overlayVisible, serviceInitialized, loadHazards]);

  // Update map markers when hazards change
  useEffect(() => {
    console.log(`🎯 Marker update useEffect triggered - map: ${!!map}, overlayVisible: ${overlayVisible}, filteredHazards: ${filteredHazards.length}, currentMarkers: ${markersRef.current.length}`);
    
    if (map && overlayVisible) {
      // Check if this is the initial load (no markers exist yet)
      const isInitialLoad = markersRef.current.length === 0 && filteredHazards.length > 0;
      
      console.log(`🎯 Load type check - isInitialLoad: ${isInitialLoad}`);
      
      if (isInitialLoad) {
        // For initial load, execute immediately
        console.log('🎯 Initial marker load - executing immediately');
      updateMapMarkers();
    } else {
        // For subsequent updates, add debouncing to prevent rapid successive updates
        console.log('🎯 Subsequent update - debouncing for 300ms');
        if (updateMarkersTimeoutRef.current) {
          clearTimeout(updateMarkersTimeoutRef.current);
        }
        
        updateMarkersTimeoutRef.current = setTimeout(() => {
          console.log('🎯 Debounced update executing now');
          updateMapMarkers();
        }, 300); // Wait 300ms before updating markers
      }
    } else {
      console.log('🎯 Clearing hazard markers - map or overlay not visible');
      clearHazardMarkers();
    }
    
    return () => {
      if (updateMarkersTimeoutRef.current) {
        clearTimeout(updateMarkersTimeoutRef.current);
      }
    };
  }, [filteredHazards, map, overlayVisible, updateMapMarkers, clearHazardMarkers]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      console.log('🚨 PROGRESSIVE REFRESH: Starting progressive hazard data refresh...');
      // 🚀 CRITICAL: Use progressive callback for real-time updates
      await hazardDataService.refreshAllSources(onProgressiveUpdate);
      // Final load to ensure everything is up to date
      loadHazards();
    } catch (error) {
      console.error('🚨 Error refreshing hazard data:', error);
      setError('Failed to refresh hazard data');
    } finally {
      setLoading(false);
    }
  };

  const handleTypeFilter = (types) => {
    setSelectedTypes(types);
  };

  const handleSeverityFilter = (severity) => {
    setMinSeverity(severity);
  };

  const toggleOverlay = () => {
    // 🚨 MOBILE FIX: Only toggle internal state if not controlled by props
    if (overlayVisibleProp === null) {
      setOverlayVisibleState(!overlayVisibleState);
    }
  };

  // 🚀 NEW: Low severity toggle handler with logging
  const toggleLowSeverity = (checked) => {
    console.log(`🔵 Low severity toggle changed: ${checked ? 'ON' : 'OFF'}`);
    setShowLowSeverity(checked);
  };

  // Test function to debug HTML parsing on current hazards
  const testCurrentHazardParsing = useCallback(() => {
    console.log('🧪 Testing HTML parsing on current hazards:');
    const htmlHazards = filteredHazards.filter(h => h.description && /<[^>]+>/g.test(h.description));
    console.log(`Found ${htmlHazards.length} hazards with HTML content:`);
    
    htmlHazards.slice(0, 3).forEach((hazard, index) => {
      console.log(`\n📝 Hazard ${index + 1}: ${hazard.title}`);
      console.log('Original:', hazard.description.substring(0, 150) + '...');
      console.log('Parsed:', parseHazardDescription(hazard.description).substring(0, 150) + '...');
      console.log('Formatted:', formatDescriptionForDisplay(parseHazardDescription(hazard.description)).substring(0, 150) + '...');
    });
    
    if (htmlHazards.length === 0) {
      console.log('No HTML hazards found in current filteredHazards');
    }
  }, [filteredHazards, parseHazardDescription, formatDescriptionForDisplay]);

  // Expose test function to window for console access
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.testCurrentHazardParsing = testCurrentHazardParsing;
      return () => {
        delete window.testCurrentHazardParsing;
      };
    }
  }, [testCurrentHazardParsing]);

  // 🌦️ ENHANCED: Weather Layer useEffect Hooks with WeatherLayerManager
  // Initialize weather manager when map becomes available
  useEffect(() => {
    if (map) {
      console.log('🌦️ Initializing enhanced weather manager...');
      initializeWeatherManager();
    }

    // Cleanup weather manager on unmount
    return () => {
      if (weatherManagerRef.current) {
        weatherManagerRef.current.destroy();
        weatherManagerRef.current = null;
      }
    };
  }, [map, initializeWeatherManager]);

  // 🚨 CRITICAL FIX: Always render hazards on map, only controls UI is optional
  // Component must stay mounted for useEffect hooks to render hazards
  
  // 📱 MOBILE DETECTION: Mobile-specific styling and behavior
  const isMobile = window.innerWidth <= 768;

  return (
    <>
      {/* Hazard markers are rendered via useEffect hooks above - always active */}
      
      {showControls && (
    <Card 
      size="small" 
      title={
        <Space>
          <WarningOutlined style={{ color: '#fa8c16' }} />
          <span>Hazard Monitoring</span>
          <Badge count={filteredHazards.length} showZero style={{ backgroundColor: '#fa8c16' }} />
        </Space>
      }
      extra={
        <Space>
          <Tooltip title={overlayVisible ? 'Hide hazards' : 'Show hazards'}>
            <Switch
              checked={overlayVisible}
              onChange={toggleOverlay}
              checkedChildren={<EyeOutlined />}
              unCheckedChildren={<EyeInvisibleOutlined />}
              size="small"
              disabled={overlayVisibleProp !== null}
            />
          </Tooltip>
          <Tooltip title="Refresh hazard data">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            />
          </Tooltip>
        </Space>
      }
      style={{ 
        marginBottom: 16,
        // 📱 MOBILE FIX: Remove all height constraints for natural sizing
        ...(isMobile && {
          height: 'auto',
          maxHeight: 'none',
          minHeight: 'auto',
          position: 'static', // Changed from relative to static for natural flow
          zIndex: 'auto'
        })
      }}
      styles={{ 
        body: { 
          padding: '12px',
          // 📱 MOBILE FIX: Remove all height and overflow constraints
          ...(isMobile && {
            height: 'auto',
            maxHeight: 'none',
            minHeight: 'auto',
            overflow: 'visible'
          })
        } 
      }}
    >
      {error && (
        <Alert 
          message={error} 
          type="error" 
          showIcon 
          closable 
          onClose={() => setError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#666' }}>
            Filters:
          </span>
        </div>
        <Select
          mode="multiple"
          size="small"
          style={{ width: '100%' }}
          placeholder="Select filters"
          value={selectedTypes}
          onChange={handleTypeFilter}
          maxTagCount={2}
          maxTagTextLength={8}
          // 📱 MOBILE FIX: Render dropdown in document body to prevent cutoff
          getPopupContainer={isMobile ? 
            () => document.body : 
            undefined
          }
          dropdownStyle={isMobile ? {
            maxHeight: '300px',
            overflowY: 'auto',
            zIndex: 2000
          } : undefined}
        >
          <Option value={HAZARD_TYPES.WEATHER}>
            <Space><ThunderboltOutlined />Weather</Space>
          </Option>
          <Option value={HAZARD_TYPES.FIRE}>
            <Space><FireOutlined />Fire</Space>
          </Option>
          <Option value={HAZARD_TYPES.BURN_OFF}>
            <Space>🔥 Burn Offs and Planned Burns</Space>
          </Option>
          <Option value={HAZARD_TYPES.EARTHQUAKE}>Earthquake</Option>
          <Option value={HAZARD_TYPES.FLOOD}>Flood</Option>
          <Option value={HAZARD_TYPES.CYCLONE}>Cyclone</Option>
          <Option value={HAZARD_TYPES.TSUNAMI}>Tsunami</Option>
          <Option value={HAZARD_TYPES.AIR_QUALITY}>Air Quality</Option>
          <Option value={HAZARD_TYPES.TRAFFIC}>
            <Space><CarOutlined />Traffic (Incidents + Live Flow)</Space>
          </Option>
          <Option value={HAZARD_TYPES.TRANSPORT}>Transport</Option>
          <Option value={HAZARD_TYPES.EMERGENCY}>Emergency</Option>
          <Option value={HAZARD_TYPES.AMBULANCE}>🚑 Ambulance</Option>
        </Select>
      </div>

      {/* 🌦️ ENHANCED: Weather Layer Controls with Time-Stepping */}
      {weatherManagerRef.current?.isSupported && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#666' }}>
              <Space>
                <CloudOutlined style={{ color: '#1890ff' }} />
                Live Weather + Forecast
                {activeWeatherLayers.length > 0 && (
                  <Badge count={activeWeatherLayers.length} size="small" style={{ backgroundColor: '#1890ff' }} />
                )}
              </Space>
            </span>
          </div>
          {/* 📱 MOBILE FIX: Define mobile detection for consistent dropdown behavior */}
          {(() => {
            const isMobile = window.innerWidth <= 768;
            return (
              <>
          {/* Weather Layer Selection */}
          <div style={{ marginBottom: '12px' }}>
                      <Select
                        mode="multiple"
                        size="small"
                        style={{ width: '100%' }}
                        placeholder="Select weather layers"
                        value={activeWeatherLayers}
                        onChange={handleWeatherLayerChange}
                        maxTagCount={2}
                        maxTagTextLength={8}
                        // 📱 MOBILE FIX: Render dropdown in document body to prevent cutoff
                        getPopupContainer={isMobile ? 
                          () => document.body : 
                          undefined
                        }
                        dropdownStyle={isMobile ? {
                          maxHeight: '300px',
                          overflowY: 'auto',
                          zIndex: 2000
                        } : undefined}
                      >
                        {Object.entries(WLM_WEATHER_LAYERS).map(([key, config]) => (
                          <Option key={key} value={key}>
                            <Space>
                              <span>{config.icon}</span>
                              <span>{config.name}</span>
                            </Space>
                          </Option>
                        ))}
                      </Select>
                    </div>

                    {/* Time Step Controls */}
                    {activeWeatherLayers.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ marginBottom: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#666', fontWeight: 500 }}>
                            Time Step:
                          </span>
                        </div>
                        <Radio.Group
                          size="small"
                          value={currentTimeStep}
                          onChange={(e) => handleTimeStepChange(e.target.value)}
                          buttonStyle="solid"
                        >
                          {Object.entries(TIME_STEPS).map(([key, config]) => (
                            <Radio.Button key={key} value={key} style={{ fontSize: '11px' }}>
                              {config.label}
                            </Radio.Button>
                          ))}
                        </Radio.Group>
                      </div>
                    )}

                    {/* Animation Controls */}
                    {activeWeatherLayers.length > 0 && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Button
                          size="small"
                          type={isWeatherAnimating ? "primary" : "default"}
                          icon={isWeatherAnimating ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                          onClick={handleWeatherAnimation}
                          style={{ fontSize: '11px' }}
                        >
                          {isWeatherAnimating ? 'Pause' : 'Play'} Animation
                        </Button>
                        <Tooltip title="Cycles through Now → +3h → +6h every 2 seconds">
                          <InfoCircleOutlined style={{ color: '#999', fontSize: '12px' }} />
                        </Tooltip>
                      </div>
                    )}
              </>
            );
          })()}
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#666' }}>
            Minimum Severity:
          </span>
        </div>
        <Select
          size="small"
          style={{ width: '100%' }}
          value={minSeverity}
          onChange={handleSeverityFilter}
          // 📱 MOBILE FIX: Render dropdown in document body to prevent cutoff
          getPopupContainer={isMobile ? 
            () => document.body : 
            undefined
          }
          dropdownStyle={isMobile ? {
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 2000
          } : undefined}
        >
          <Option value={2}>
            <span style={{ color: '#fa8c16' }}>🟡 Moderate</span>
          </Option>
          <Option value={3}>
            <span style={{ color: '#f5222d' }}>🔴 High</span>
          </Option>
          <Option value={4}>
            <span style={{ color: '#722ed1' }}>🟣 Extreme</span>
          </Option>
        </Select>
      </div>



      {/* 🚀 NEW: Low Severity Toggle Feature */}
      {lowSeverityFeatureEnabled && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '8px 12px',
            background: showLowSeverity ? '#f6ffed' : '#f5f5f5',
            border: `1px solid ${showLowSeverity ? '#b7eb8f' : '#d9d9d9'}`,
            borderRadius: '6px',
            marginBottom: '4px'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 500, color: '#666', marginBottom: '2px' }}>
                🔵 Show Low Severity Incidents
              </div>
              <div style={{ fontSize: '11px', color: '#999' }}>
                Shows low severity hazards/incidents
              </div>
            </div>
            <Switch
              size="small"
              checked={showLowSeverity}
              onChange={toggleLowSeverity}
              style={{
                backgroundColor: showLowSeverity ? '#52c41a' : undefined
              }}
            />
          </div>

        </div>
      )}

      {/* 🚀 NEW: Site Icons Toggle Feature */}
      {onShowSiteIconsChange && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '8px 12px',
            background: showSiteIcons ? '#f6ffed' : '#f5f5f5',
            border: `1px solid ${showSiteIcons ? '#b7eb8f' : '#d9d9d9'}`,
            borderRadius: '6px',
            marginBottom: '4px'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 500, color: '#666', marginBottom: '2px' }}>
                🏢 Show Organisation Sites
              </div>
              <div style={{ fontSize: '11px', color: '#999' }}>
                Shows/hides site markers and legend
              </div>
            </div>
            <Switch
              size="small"
              checked={showSiteIcons}
              onChange={onShowSiteIconsChange}
              style={{
                backgroundColor: showSiteIcons ? '#52c41a' : undefined
              }}
            />
          </div>

        </div>
      )}

      {/* 🎨 NEW: Site Color Mode Toggle Feature */}
      {onSiteColorModeChange && showSiteIcons && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '8px 12px',
            background: siteColorMode === 'status' ? '#fff7e6' : '#f0f5ff',
            border: `1px solid ${siteColorMode === 'status' ? '#ffd591' : '#adc6ff'}`,
            borderRadius: '6px',
            marginBottom: '4px'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 500, color: '#666', marginBottom: '2px' }}>
                🎨 {siteColorMode === 'status' ? 'Color by Status' : 'Color by Type'}
              </div>
              <div style={{ fontSize: '11px', color: '#999' }}>
                {siteColorMode === 'status' ? 'Shows site operational status' : 'Shows site type categories'}
              </div>
            </div>
            <Switch
              size="small"
              checked={siteColorMode === 'status'}
              onChange={(checked) => onSiteColorModeChange(checked ? 'status' : 'type')}
              style={{
                backgroundColor: siteColorMode === 'status' ? '#fa8c16' : undefined
              }}
            />
          </div>

        </div>
      )}

      {lastUpdate && (
        <div style={{ fontSize: '11px', color: '#999', textAlign: 'center' }}>
          Last updated: {new Date(lastUpdate).toLocaleTimeString()}
        </div>
      )}
    </Card>
      )}

      {/* 🌦️ Weather Legend Overlay */}
      <WeatherLegend
        legends={weatherLegends}
        timeStep={currentTimeStep}
        visible={weatherLegends.length > 0}
      />

      {/* 🌦️ Weather Popup for Site Details */}
      <WeatherPopup
        weatherManager={weatherManagerRef.current}
        latitude={weatherPopupData?.latitude}
        longitude={weatherPopupData?.longitude}
        siteName={weatherPopupData?.siteName}
        visible={weatherPopupVisible}
        onClose={() => setWeatherPopupVisible(false)}
      />
    </>
  );
};

export default HazardOverlayLayer; 